import styled from '@xstyled/styled-components'
import type { ColorSchemes } from '../../theme/core/colors'

export type RadioButtonSize = 'sm' | 'md' | 'lg' | 'xl'
export type RadioButtonSizes = Record<
  RadioButtonSize,
  Record<'radio' | 'font', number>
>

const SIZES: RadioButtonSizes = {
  sm: { radio: 12, font: 14 },
  md: { radio: 16, font: 15 },
  lg: { radio: 20, font: 15 },
  xl: { radio: 24, font: 15 }
}

export interface RadioButtonContentProps {
  colorScheme: ColorSchemes
}

export const RadioButtonContent = styled.divBox<RadioButtonContentProps>(
  props => ({
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'var(--mote-radio-button-size)',
    height: 'var(--mote-radio-button-size)',
    minHeight: 'var(--mote-radio-button-size)',
    minWidth: 'var(--mote-radio-button-size)',
    padding: '0',
    borderRadius: '100%',
    flexShrink: 0,
    boxSizing: 'border-box',
    transitionProperty: 'box-shadow, opacity',
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease-in',
    '&[data-focused]': {
      boxShadow: props.theme.shadows[`outline-${props.colorScheme}`]
    },
    '&[data-disabled]': {
      cursor: 'not-allowed',
      '& span.radiobutton-check': {
        borderColor: props.theme.colors.disabled.l1,
        backgroundColor: props.theme.colors.disabled.l1
      },
      '&:hover, &:active': {
        '& span.radiobutton-check': {
          borderColor: props.theme.colors.disabled.l1,
          backgroundColor: props.theme.colors.disabled.l1
        }
      },
      '&[data-checked]': {
        '& span.radiobutton-check': {
          borderColor: props.theme.colors.disabled.l1,
          backgroundColor: props.theme.colors.disabled.l2
        },
        '&:hover, &:active': {
          '& span.radiobutton-check': {
            borderColor: props.theme.colors.disabled.l1,
            backgroundColor: props.theme.colors.disabled.l2
          }
        }
      }
    },
    '&[data-checked]': {
      '& span.radiobutton-check': {
        borderWidth: 'var(--mote-radio-button-border-width)',
        borderColor: props.theme.colors[props.colorScheme].base
      },
      '&:hover': {
        '& span.radiobutton-check': {
          borderColor: props.theme.colors[props.colorScheme].d1
        }
      },
      '&:active': {
        '& span.radiobutton-check': {
          borderColor: props.theme.colors[props.colorScheme].d2
        }
      }
    },
    '&[data-invalid]': {
      '& span.radiobutton-check': { borderColor: props.theme.colors.error }
    }
  })
)

export interface RadioButtonRootProps {
  size: RadioButtonSize
}

export const RadioButtonContainer = styled.labelBox<RadioButtonRootProps>(
  props => ({
    '--mote-radio-button-size': `${SIZES[props.size].radio}px`,
    '--mote-radio-button-font-size': `${SIZES[props.size].font}px`,
    '--mote-radio-button-border-width': `${SIZES[props.size].radio * 0.34}px`,
    '--mote-radio-button-border-unchecked-color': `${props.theme.colors.grey.l2}`,
    '--mote-radio-button-background': `${props.theme.colors.white}`
  }),
  {
    display: 'inline-flex',
    justifyContent: 'flex-start',
    verticalAlign: 'top',
    alignItems: 'center',
    position: 'relative',
    width: 'auto',
    gap: '8px',
    transition: 'all 150ms ease',
    '& span.radiobutton-text': { opacity: 1 },
    '&[data-disabled]': {
      cursor: 'not-allowed',
      '& span.radiobutton-text': { opacity: 0.4 }
    }
  }
)

export const RadioButtonLabel = styled.spanBox({
  display: 'inline-block',
  fontSize: 'var(--mote-radio-button-font-size)',
  lineHeight: '120%',
  fontStyle: 'normal',
  fontWeight: 400,
  color: 'black',
  userSelect: 'none'
})

export const CheckedDot = styled.spanBox(props => ({
  display: 'block',
  height: '100%',
  width: '100%',
  boxSizing: 'border-box',
  border: '2px solid var(--mote-radio-button-border-unchecked-color)',
  borderRadius: '100%',
  backgroundColor: 'var(--mote-radio-button-background)',
  transitionDuration: '150ms',
  transitionTimingFunction: 'ease-in',
  transitionProperty: 'border-width, border-color'
}))
